import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
} from "react";
import {
  getAllInvoices,
  createInvoice,
  updateInvoice as updateInvoiceService,
  deleteInvoice as deleteInvoiceService,
  getInvoiceById,
  getInvoiceByNumber,
} from "../api/services/invoiceService";
import { useAuth } from "./AuthContext";

const InvoiceContext = createContext();

export const useInvoice = () => useContext(InvoiceContext);

export const InvoiceProvider = ({ children }) => {
  const { user } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Add a cache to prevent repeated lookups
  const invoiceCache = useRef(new Map());
  // Add a processing flag to prevent multiple operations on the same invoice
  const processingInvoices = useRef(new Set());

  const fetchInvoices = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const data = await getAllInvoices();
      setInvoices(data);

      // Update cache with fetched invoices
      data.forEach((invoice) => {
        if (invoice._id) {
          invoiceCache.current.set(invoice._id.toString(), invoice);
        }
        if (invoice.id) {
          invoiceCache.current.set(invoice.id.toString(), invoice);
        }
        if (invoice.number) {
          invoiceCache.current.set(invoice.number.toString(), invoice);
        }
      });

      setError(null);
    } catch (error) {
      console.error("Error fetching invoices:", error);

      // Safely extract error message
      let errorMessage = "Failed to fetch invoices";
      if (error && typeof error === "object") {
        if (error.message) {
          errorMessage = error.message;
        } else if (error.error) {
          errorMessage = error.error;
        } else if (typeof error === "string") {
          errorMessage = error;
        }
      } else if (typeof error === "string") {
        errorMessage = error;
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Guard against React StrictMode double-invoke and upstream context
  // re-renders that keep re-firing `[user]` with a fresh reference.
  const invoicesFetchedForUserId = useRef(null);
  useEffect(() => {
    const uid = user?.id || user?._id || null;
    if (!uid) { invoicesFetchedForUserId.current = null; return; }
    if (invoicesFetchedForUserId.current !== uid) {
      invoicesFetchedForUserId.current = uid;
      fetchInvoices();
    }
    return () => {
      invoiceCache.current.clear();
      processingInvoices.current.clear();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const generateInvoiceNumber = () => {
    const prefix = "INV";
    const lastInvoice = invoices[invoices.length - 1];
    if (!lastInvoice) {
      return `${prefix}-001`;
    }
    const lastNumber = parseInt(lastInvoice.number.split("-")[1]);
    const newNumber = (lastNumber + 1).toString().padStart(3, "0");
    return `${prefix}-${newNumber}`;
  };

  const addInvoice = async (invoiceData) => {
    try {
      const newInvoice = {
        ...invoiceData,
        number: generateInvoiceNumber(),
        paid: "0.00",
        status: invoiceData.status || "Pending",
        expiredDate: new Date(
          new Date(invoiceData.date).getTime() + 30 * 24 * 60 * 60 * 1000
        )
          .toISOString()
          .split("T")[0],
      };

      // Use the invoiceService to create the invoice
      const createdInvoice = await createInvoice(newInvoice);

      // Update state and cache
      setInvoices((prevInvoices) => [...prevInvoices, createdInvoice]);

      if (createdInvoice._id) {
        invoiceCache.current.set(createdInvoice._id.toString(), createdInvoice);
      }
      if (createdInvoice.id) {
        invoiceCache.current.set(createdInvoice.id.toString(), createdInvoice);
      }
      if (createdInvoice.number) {
        invoiceCache.current.set(
          createdInvoice.number.toString(),
          createdInvoice
        );
      }

      return createdInvoice;
    } catch (error) {
      console.error("Error adding invoice:", error);
      throw error;
    }
  };

  const updateInvoice = async (id, updatedInvoice) => {
    try {
      // Prevent multiple updates for the same invoice
      if (processingInvoices.current.has(id.toString())) {
        console.log(
          `Invoice ${id} is already being processed, skipping update`
        );
        return null;
      }

      processingInvoices.current.add(id.toString());

      // Use the invoiceService to update the invoice
      const result = await updateInvoiceService(id, updatedInvoice);

      setInvoices((prevInvoices) =>
        prevInvoices.map((invoice) =>
          invoice._id === id || invoice.id === id ? result : invoice
        )
      );

      // Update cache
      if (result._id) {
        invoiceCache.current.set(result._id.toString(), result);
      }
      if (result.id) {
        invoiceCache.current.set(result.id.toString(), result);
      }
      if (result.number) {
        invoiceCache.current.set(result.number.toString(), result);
      }

      processingInvoices.current.delete(id.toString());
      return result;
    } catch (error) {
      processingInvoices.current.delete(id.toString());
      console.error("Error updating invoice:", error);
      throw error;
    }
  };

  const deleteInvoice = async (invoiceId) => {
    try {
      if (processingInvoices.current.has(invoiceId.toString())) {
        console.log(
          `Invoice ${invoiceId} is already being processed, skipping delete`
        );
        return;
      }

      processingInvoices.current.add(invoiceId.toString());

      // Use the invoiceService to delete the invoice
      await deleteInvoiceService(invoiceId);

      // Remove from state
      setInvoices((prevInvoices) =>
        prevInvoices.filter(
          (invoice) => invoice._id !== invoiceId && invoice.id !== invoiceId
        )
      );

      // Remove from cache
      invoiceCache.current.delete(invoiceId.toString());

      processingInvoices.current.delete(invoiceId.toString());
    } catch (error) {
      processingInvoices.current.delete(invoiceId.toString());
      console.error("Error deleting invoice:", error);
      throw error;
    }
  };

  // Synchronous getter (cache/state only) to preserve existing usage
  const getInvoice = (id) => {
    if (!id) {
      console.warn("getInvoice called with null/undefined id");
      return null;
    }

    const idString = id.toString();

    if (invoiceCache.current.has(idString)) {
      return invoiceCache.current.get(idString);
    }

    const localInvoice = invoices.find(
      (inv) =>
        (inv._id && inv._id.toString() === idString) ||
        (inv.id && inv.id.toString() === idString) ||
        (inv.number && inv.number.toString() === idString) ||
        (inv.billNumber && inv.billNumber.toString() === idString)
    );

    if (localInvoice) {
      invoiceCache.current.set(idString, localInvoice);
      return localInvoice;
    }

    return null;
  };

  // Async getter with API fallback for routes like ShowInvoice
  const getInvoiceAsync = async (id) => {
    if (!id) {
      console.warn("getInvoice called with null/undefined id");
      return null;
    }

    const idString = id.toString();

    // Check cache first
    if (invoiceCache.current.has(idString)) {
      return invoiceCache.current.get(idString);
    }

    // If not in cache, look in current state (but only once)
    const localInvoice = invoices.find(
      (inv) =>
        (inv._id && inv._id.toString() === idString) ||
        (inv.id && inv.id.toString() === idString) ||
        (inv.number && inv.number.toString() === idString) ||
        (inv.billNumber && inv.billNumber.toString() === idString)
    );

    if (localInvoice) {
      invoiceCache.current.set(idString, localInvoice);
      return localInvoice;
    }

    // Not found in memory, try API lookup by ID first, then by number
    try {
      // Try by ID
      const apiById = await getInvoiceById(idString);
      if (apiById) {
        invoiceCache.current.set(idString, apiById);
        setInvoices((prev) => {
          const exists = prev.some(
            (inv) =>
              (inv._id && String(inv._id) === String(apiById._id)) ||
              (inv.id && String(inv.id) === String(apiById.id)) ||
              (inv.number && String(inv.number) === String(apiById.number))
          );
          return exists ? prev : [...prev, apiById];
        });
        return apiById;
      }
    } catch (e) {
      // ignore and try by number
    }

    try {
      const apiByNumber = await getInvoiceByNumber(idString);
      if (apiByNumber) {
        invoiceCache.current.set(idString, apiByNumber);
        setInvoices((prev) => {
          const exists = prev.some(
            (inv) =>
              (inv._id && String(inv._id) === String(apiByNumber._id)) ||
              (inv.id && String(inv.id) === String(apiByNumber.id)) ||
              (inv.number && String(inv.number) === String(apiByNumber.number))
          );
          return exists ? prev : [...prev, apiByNumber];
        });
        return apiByNumber;
      }
    } catch (e) {
      // fallthrough
    }

    return null;
  };

  const clearInvoices = () => {
    setInvoices([]);
    invoiceCache.current.clear();
    processingInvoices.current.clear();
  };

  const addPayment = (invoiceId, paymentData) => {
    setInvoices((prevInvoices) => {
      return prevInvoices.map((invoice) => {
        if (invoice._id === invoiceId || invoice.id === invoiceId) {
          const currentPaid = parseFloat(invoice.paid || 0);
          const newPaid = currentPaid + parseFloat(paymentData.amount);
          const total = parseFloat(invoice.total);

          // Update payment status based on paid amount
          let newStatus;
          if (newPaid >= total) {
            newStatus = "Paid";
          } else if (newPaid > 0) {
            newStatus = "Partially Paid";
          } else {
            newStatus = invoice.status;
          }

          const updatedInvoice = {
            ...invoice,
            paid: newPaid.toFixed(2),
            status: newStatus,
            payments: [
              ...(invoice.payments || []),
              { ...paymentData, number: generatePaymentNumber() },
            ],
          };

          // Update cache
          if (invoice._id) {
            invoiceCache.current.set(invoice._id.toString(), updatedInvoice);
          }
          if (invoice.id) {
            invoiceCache.current.set(invoice.id.toString(), updatedInvoice);
          }

          return updatedInvoice;
        }
        return invoice;
      });
    });
  };

  const generatePaymentNumber = () => {
    const prefix = "PAY";
    const timestamp = Date.now();
    return `${prefix}-${timestamp}`;
  };

  return (
    <InvoiceContext.Provider
      value={{
        invoices,
        addInvoice,
        updateInvoice,
        deleteInvoice,
        getInvoice,
        getInvoiceAsync,
        clearInvoices,
        fetchInvoices,
        addPayment,
      }}
    >
      {children}
    </InvoiceContext.Provider>
  );
};
